import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import {
  Clear as ClearIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const SignaturePad = ({ onSignature, label, language = 'en' }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [context, setContext] = useState(null);

  const labels = {
    en: {
      signHere: 'Sign Here',
      clear: 'Clear',
      instructions: 'Draw your signature using your mouse or touchscreen',
      signed: 'Signature captured'
    },
    es: {
      signHere: 'Firme Aquí',
      clear: 'Borrar',
      instructions: 'Dibuje su firma usando el mouse o la pantalla táctil',
      signed: 'Firma capturada'
    }
  };

  const t = labels[language] || labels.en;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    setContext(ctx);
  }, []);

  const startDrawing = (e) => {
    if (!context) return;
    
    setIsDrawing(true);
    const { offsetX, offsetY } = getCoordinates(e);
    context.beginPath();
    context.moveTo(offsetX, offsetY);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;
    
    e.preventDefault();
    const { offsetX, offsetY } = getCoordinates(e);
    context.lineTo(offsetX, offsetY);
    context.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (!context) return;
    
    setIsDrawing(false);
    context.closePath();
    
    if (hasSignature) {
      // Export signature as base64 PNG
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL('image/png');
      onSignature(dataURL);
    }
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches && e.touches.length > 0) {
      // Touch event
      return {
        offsetX: e.touches[0].clientX - rect.left,
        offsetY: e.touches[0].clientY - rect.top
      };
    } else {
      // Mouse event
      return {
        offsetX: e.nativeEvent.offsetX,
        offsetY: e.nativeEvent.offsetY
      };
    }
  };

  const clearSignature = () => {
    if (!context) return;
    
    const canvas = canvasRef.current;
    context.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignature(null);
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {label || t.signHere}
      </Typography>
      
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t.instructions}
      </Typography>
      
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          border: '2px solid',
          borderColor: hasSignature ? 'success.main' : 'grey.300',
          borderRadius: 1,
          overflow: 'hidden',
          touchAction: 'none'
        }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={label || t.signHere}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{
            width: '100%',
            height: '150px',
            cursor: 'crosshair',
            display: 'block'
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8
          }}
        >
          <IconButton
            onClick={clearSignature}
            aria-label={t.clear}
            size="small"
            sx={{
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
      
      {hasSignature && (
        <Alert 
          severity="success" 
          icon={<CheckIcon />}
          sx={{ mt: 1 }}
        >
          {t.signed}
        </Alert>
      )}
    </Box>
  );
};

export default SignaturePad;

